import React, { useEffect, useState, useCallback, useRef } from 'react';
import { GraduationCap, Plus, Search, Eye, EyeOff, Pencil, Trash2, Upload, Camera, Sparkles, Wand2, KeyRound, Loader2, X, RefreshCw, Eraser, UserPlus, Printer, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrgId } from '../../hooks/useOrgId';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { useT } from '../../i18n/LanguageContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card } from '../../components/school/ui';
import { Pagination, SortableTH } from '../../components/school/Pagination';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { PICKABLE_ROLES } from '../../lib/roles';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== Student ID Card (credential card) helpers ====================
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LOGIN_URL = `${process.env.REACT_APP_BACKEND_URL}`;

const resolveLogo = (org) => {
  const u = org?.logo_url;
  if (!u) return '/eli-crest.png';
  return u.startsWith('http') ? u : `${BACKEND}${u}`;
};

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

const USER_SVG = '<svg viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';
const LOCK_SVG = '<svg viewBox="0 0 24 24"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';
const GLOBE_SVG = '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>';

const CARD_CSS = `
.tcard { width: 430px; box-sizing: border-box; border-radius: 16px; overflow: hidden; background: #fff; border: 3px solid #16316b; position: relative; color: #16316b; }
.tcard * { box-sizing: border-box; }
.tcard-top { position: relative; display: flex; align-items: center; gap: 12px; padding: 18px 20px 14px; background: #fff; overflow: hidden; }
.tcard-top::after { content: ''; position: absolute; top: -40px; right: -60px; width: 170px; height: 150px; background: #16316b; transform: rotate(32deg); opacity: 0.08; }
.tcard-logo { width: 46px; height: 46px; object-fit: contain; flex: none; z-index: 1; }
.tcard-school-name { font-size: 20px; font-weight: 800; letter-spacing: 1px; line-height: 1.1; z-index: 1; }
.tcard-school-sub { font-size: 10px; font-weight: 600; letter-spacing: 3px; color: #5b6b8c; margin-top: 3px; }
.tcard-divider { height: 3px; background: linear-gradient(90deg, #16316b 62%, #c8a24a 62%); }
.tcard-body { padding: 14px 20px 16px; }
.tcard-type { font-size: 11px; font-weight: 800; letter-spacing: 2px; color: #c8a24a; text-transform: uppercase; }
.tcard-name { font-size: 24px; font-weight: 800; color: #16316b; margin: 2px 0 2px; line-height: 1.1; }
.tcard-adm { font-size: 12px; font-weight: 700; color: #5b6b8c; font-family: 'Source Code Pro', monospace; margin-bottom: 4px; }
.tcard-class { font-size: 12px; font-weight: 700; color: #16316b; margin-bottom: 10px; }
.tcard-creds { background: #f4f5f7; border-radius: 12px; border-left: 5px solid #c8a24a; padding: 12px 14px; display: flex; flex-direction: column; gap: 10px; }
.cred-row { display: flex; align-items: center; gap: 12px; }
.cred-tile { width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex: none; }
.cred-tile.user { background: #16316b; }
.cred-tile.lock { background: #c0392b; }
.cred-tile svg { width: 18px; height: 18px; stroke: #fff; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.cred-label { font-size: 9px; font-weight: 700; letter-spacing: 1.5px; color: #8a93a6; text-transform: uppercase; }
.cred-val { font-size: 15px; font-weight: 700; color: #16316b; font-family: 'Source Code Pro', monospace; word-break: break-all; }
.tcard-footer { background: #16316b; color: #fff; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 10px; }
.tcard-footer svg { width: 15px; height: 15px; stroke: #fff; fill: none; stroke-width: 2; }
.tcard-footer .portal { color: #c8a24a; font-weight: 700; letter-spacing: 2px; font-size: 11px; }
.tcard-footer .domain { font-weight: 800; font-family: 'Source Code Pro', monospace; font-size: 13px; }
`;

const cardMarkup = (org, cred) => {
  const logo = resolveLogo(org);
  const name = escapeHtml(cred?.name || '');
  const adm = escapeHtml(cred?.admission_no || '');
  const email = escapeHtml(cred?.email || '');
  const pass = escapeHtml(cred?.password || '');
  const cls = escapeHtml(cred?.class_name || '');
  const sec = escapeHtml(cred?.section_name || '');
  const classLine = cls ? `CLASS: ${cls}${sec ? ' / ' + sec : ''}` : '';
  const schoolName = escapeHtml(org?.name || 'E.L.I');
  const loginUrl = escapeHtml((LOGIN_URL || '').replace(/^https?:\/\//, '') || 'eli-school.com');
  return `
  <div class="tcard">
    <div class="tcard-top">
      <img class="tcard-logo" src="${logo}" alt="logo" onerror="this.style.display='none'"/>
      <div>
        <div class="tcard-school-name">${schoolName}</div>
        <div class="tcard-school-sub">STUDENT PORTAL</div>
      </div>
    </div>
    <div class="tcard-divider"></div>
    <div class="tcard-body">
      <div class="tcard-type">STUDENT ID CARD</div>
      <div class="tcard-name">${name}</div>
      <div class="tcard-adm">${adm ? 'ADM: ' + adm : ''}</div>
      ${classLine ? `<div class="tcard-class">${classLine}</div>` : ''}
      <div class="tcard-creds">
        <div class="cred-row">
          <span class="cred-tile user">${USER_SVG}</span>
          <div><div class="cred-label">USERNAME</div><div class="cred-val">${email || '\u2014'}</div></div>
        </div>
        <div class="cred-row">
          <span class="cred-tile lock">${LOCK_SVG}</span>
          <div><div class="cred-label">PASSWORD</div><div class="cred-val">${pass || '\u2014'}</div></div>
        </div>
      </div>
    </div>
    <div class="tcard-footer">${GLOBE_SVG}<span class="portal">LOGIN</span><span class="domain">${loginUrl}</span></div>
  </div>`;
};

const buildCredentialCardsHtml = (org, creds) => {
  const cards = (creds || []).map((c) => cardMarkup(org, c)).join('');
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
  <title>Student Credential Cards</title>
  <link href="https://fonts.googleapis.com/css2?family=Figtree:wght@400;600;700;800&family=Source+Code+Pro:wght@600;700&display=swap" rel="stylesheet"/>
  <style>
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { margin: 0; padding: 20px; background: #eef0f4; font-family: 'Figtree', system-ui, sans-serif; }
    .grid { display: flex; flex-wrap: wrap; gap: 20px; }
    .tcard { page-break-inside: avoid; }
    ${CARD_CSS}
    @media print { body { background: #fff; padding: 0; } .grid { gap: 14px; } }
  </style></head>
  <body><div class="grid">${cards}</div></body></html>`;
};

// Print sheet: 8 credential cards per A4 page (2 columns x 4 rows).
const buildCredentialSheetHtml = (org, creds) => {
  const groups = [];
  for (let i = 0; i < (creds || []).length; i += 8) groups.push(creds.slice(i, i + 8));
  const pagesHtml = groups.map((group) => (
    `<div class="page"><div class="sheet">${group.map((c) => `<div class="cell">${cardMarkup(org, c)}</div>`).join('')}</div></div>`
  )).join('');
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
  <title>Student Credential Cards</title>
  <link href="https://fonts.googleapis.com/css2?family=Figtree:wght@400;600;700;800&family=Source+Code+Pro:wght@600;700&display=swap" rel="stylesheet"/>
  <style>
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    @page { size: A4 portrait; margin: 8mm; }
    body { margin: 0; padding: 0; background: #fff; font-family: 'Figtree', system-ui, sans-serif; }
    ${CARD_CSS}
    .page { width: 100%; }
    .sheet { display: grid; grid-template-columns: 1fr 1fr; grid-auto-rows: 64mm; gap: 4mm; }
    .cell { width: 92mm; height: 63mm; overflow: hidden; }
    .cell .tcard { width: 430px; transform: scale(0.72); transform-origin: top left; border-width: 2px; }
    .page + .page { page-break-before: always; }
  </style></head>
  <body>${pagesHtml}</body></html>`;
};

const emptyForm = {
  name: '', gender: 'Male', dob: '', class_id: '', section_id: '', roll_no: '',
  blood_group: '', address: '', phone: '', email: '',
  family_account_id: '', new_family_name: '', new_family_phone: '', new_family_email: '',
  password: '', role: 'student', family_password: '', photo_url: '', special_needs: false,
};

export default function StudentsPage() {
  const { user, canAdmin, currentOrg } = useAuth();
  const { t } = useT();
  const orgId = useOrgId();
  const { selectedYearId } = useAcademicYear();

  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [onlyNotEnrolled, setOnlyNotEnrolled] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const PAGE_SIZE = 50;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [familyMode, setFamilyMode] = useState('existing'); // existing | new
  const [familySearch, setFamilySearch] = useState('');
  const [familyResults, setFamilyResults] = useState([]);
  const [selectedFamilyLabel, setSelectedFamilyLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [viewStudent, setViewStudent] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // ---- Enroll-from-registry (inline) ----
  const [enrollStudent, setEnrollStudent] = useState(null); // target student (registry)
  const [enrollForm, setEnrollForm] = useState({ class_id: '', section_id: '', roll_no: '' });
  const [enrollSections, setEnrollSections] = useState([]);
  const [enrollSaving, setEnrollSaving] = useState(false);

  // ---- Login generation + password reveal ----
  const [genLoading, setGenLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [revealCreds, setRevealCreds] = useState(null); // { email, password } for super admin
  const [revealLoading, setRevealLoading] = useState(false);
  const isSuperAdmin = user?.role === 'super_admin';

  // ---- Bulk credential generation ----
  const [credOpen, setCredOpen] = useState(false);
  const [credScan, setCredScan] = useState([]);
  const [credLoadingList, setCredLoadingList] = useState(false);
  const [credSearch, setCredSearch] = useState('');
  const [credSelected, setCredSelected] = useState({});
  const [credShowAll, setCredShowAll] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [printCards, setPrintCards] = useState(null);

  // ---- Print credentials (class/section filter -> cards) ----
  const [printFilterOpen, setPrintFilterOpen] = useState(false);
  const [printClass, setPrintClass] = useState('');
  const [printSection, setPrintSection] = useState('');
  const [printSections, setPrintSections] = useState([]);
  const [printLoadingSecs, setPrintLoadingSecs] = useState(false);
  const [reprintOpen, setReprintOpen] = useState(false);
  const [reprintList, setReprintList] = useState([]);
  const [reprintLoading, setReprintLoading] = useState(false);
  const [reprintSearch, setReprintSearch] = useState('');
  const [reprintSelected, setReprintSelected] = useState({});

  // ---- Photo (upload / webcam) + AI enhance ----
  const photoInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [photoBlob, setPhotoBlob] = useState(null); // raw File/Blob (source for AI enhance)
  const [photoPreview, setPhotoPreview] = useState(''); // displayable URL (object url or s3 url)
  const [photoUploading, setPhotoUploading] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [camOpen, setCamOpen] = useState(false);

  // ---- Excel import (background job + polling) ----
  const fileRef = useRef(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);

  const loadRefs = useCallback(async () => {
    if (!orgId) return;
    const [cls, yrs] = await Promise.all([
      schoolApi.listClasses(orgId, selectedYearId), schoolApi.listYears(orgId),
    ]);
    setClasses(cls); setYears(yrs);
  }, [orgId, selectedYearId]);

  const loadStudents = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await schoolApi.listStudents(orgId, {
        search, class_id: filterClass, sort_by: sortBy, sort_dir: sortDir,
        enrich_year: selectedYearId || (years.find((y) => y.is_active) || years[0])?.id,
        not_enrolled: onlyNotEnrolled || undefined,
        skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE,
      });
      setStudents(data.students || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error('Failed to load students'); }
    finally { setLoading(false); }
  }, [orgId, search, filterClass, sortBy, sortDir, page, years, onlyNotEnrolled, selectedYearId]);

  // One-time hygiene: strip legacy class/section/roll/year fields off student records
  const [cleaningReg, setCleaningReg] = useState(false);
  const runCleanupAcademicFields = async () => {
    setCleaningReg(true);
    try {
      const r = await schoolApi.cleanupStudentAcademicFields({ organization_id: orgId });
      toast.success(r?.message || t('students.academicCleaned', 'Legacy class fields cleaned'));
      loadStudents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('students.cleanupFailed', 'Cleanup failed'));
    } finally { setCleaningReg(false); }
  };

  // Assign the default 'Normal Guardian' (code 41110001) to students with no guardian
  const [assignGuardianOpen, setAssignGuardianOpen] = useState(false);
  const [assigningGuardian, setAssigningGuardian] = useState(false);
  const runAssignDefaultGuardian = async () => {
    setAssigningGuardian(true);
    try {
      const r = await schoolApi.assignDefaultGuardian({ organization_id: orgId });
      toast.success(r?.message || t('students.guardianAssigned', 'Default guardian assigned'));
      setAssignGuardianOpen(false);
      loadStudents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('students.guardianAssignFailed', 'Could not assign default guardian'));
    } finally { setAssigningGuardian(false); }
  };


  // Reset to first page whenever the query (search / filter / sort) changes
  useEffect(() => { setPage(1); }, [search, filterClass, sortBy, sortDir, onlyNotEnrolled]);

  const onSort = (field) => {
    if (sortBy === field) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); }
    else { setSortBy(field); setSortDir('asc'); }
  };

  useEffect(() => { loadRefs(); }, [loadRefs]);
  useEffect(() => { const id = setTimeout(loadStudents, 250); return () => clearTimeout(id); }, [loadStudents]);

  // Searchable Family/Guardian lookup (server-side)
  useEffect(() => {
    if (!orgId || familyMode !== 'existing') return;
    const id = setTimeout(async () => {
      try {
        const data = await schoolApi.listFamilies(orgId, { search: familySearch, limit: 20 });
        setFamilyResults(data.families || []);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(id);
  }, [orgId, familySearch, familyMode]);

  const activeYear = years.find((y) => y.is_active) || years[0];
  // Enrollment always targets the globally selected academic year (falling back to the
  // active year) so records created here match what the Enrollment module reads.
  const enrollYearId = selectedYearId || activeYear?.id;

  const familyDisplay = (f) => (f.name_en || f.name || '') + (f.mobile ? ` (${f.mobile})` : '') + ` · ${f.code}`;

  const openCreate = () => {
    setEditing(null); setForm(emptyForm); setFamilyMode('existing');
    setFamilySearch(''); setFamilyResults([]); setSelectedFamilyLabel(''); setSections([]);
    setShowPassword(false); setPhotoBlob(null); setPhotoPreview(''); setRevealCreds(null);
    setDialogOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...emptyForm, ...s, family_account_id: s.family_account_id || '' });
    setFamilyMode('existing');
    setFamilySearch('');
    setFamilyResults([]);
    setShowPassword(false); setPhotoBlob(null); setPhotoPreview(s.photo_url || ''); setRevealCreds(null);
    setSelectedFamilyLabel(s.family_name ? `${s.family_name}${s.family_code ? ' · ' + s.family_code : ''}` : '');
    if (s.class_id) schoolApi.listSections(orgId, s.class_id).then(setSections);
    setDialogOpen(true);
  };

  const pickFamily = (f) => {
    setForm((prev) => ({ ...prev, family_account_id: f.id }));
    setSelectedFamilyLabel(familyDisplay(f));
    setFamilyResults([]);
    setFamilySearch('');
  };

  const onClassChange = async (class_id) => {
    setForm((f) => ({ ...f, class_id, section_id: '' }));
    const secs = await schoolApi.listSections(orgId, class_id);
    setSections(secs);
  };

  // ---- Auto-generate login (firstname.lastname@eli-school.com + derived password) ----
  const generateLogin = async () => {
    if (!form.name.trim()) { toast.error(t('common.name') + ' ' + t('common.required')); return; }
    setGenLoading(true);
    try {
      const data = await schoolApi.suggestStudentLogin(orgId, form.name.trim());
      setForm((f) => ({ ...f, email: data.email || f.email, password: data.password || f.password }));
      setShowPassword(true);
      toast.success(t('students.loginGenerated', 'Login generated'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate login');
    } finally { setGenLoading(false); }
  };

  // ---- Photo: upload selected/captured blob to S3 ----
  const uploadPhoto = async (blob) => {
    if (!blob) return;
    setPhotoUploading(true);
    try {
      const file = blob instanceof File ? blob : new File([blob], 'photo.png', { type: blob.type || 'image/png' });
      const res = await schoolApi.uploadStudentPhoto(orgId, file);
      setForm((f) => ({ ...f, photo_url: res.url }));
      setPhotoPreview(res.url);
      toast.success(t('students.photoUploaded', 'Photo uploaded'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to upload photo');
    } finally { setPhotoUploading(false); }
  };

  const onPhotoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    setPhotoBlob(file);
    setPhotoPreview(URL.createObjectURL(file));
    uploadPhoto(file);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  // ---- Webcam capture ----
  const openCamera = async () => {
    setCamOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      // Wait for the dialog/video element to mount
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); } }, 100);
    } catch (e) {
      toast.error(t('students.cameraError', 'Unable to access camera. Check browser permissions.'));
      setCamOpen(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tk) => tk.stop());
      streamRef.current = null;
    }
  };

  const closeCamera = () => { stopCamera(); setCamOpen(false); };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 480;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (blob) {
        setPhotoBlob(blob);
        setPhotoPreview(URL.createObjectURL(blob));
        uploadPhoto(blob);
      }
      closeCamera();
    }, 'image/png');
  };

  useEffect(() => () => stopCamera(), []);

  // ---- AI enhance (Gemini Nano Banana) ----
  const enhancePhoto = async () => {
    let src = photoBlob;
    // If no fresh blob (e.g. editing an existing student), fetch current photo and enhance it
    if (!src && form.photo_url) {
      try {
        const resp = await fetch(form.photo_url);
        src = await resp.blob();
      } catch { /* ignore */ }
    }
    if (!src) { toast.error(t('students.noPhoto', 'Add a photo first')); return; }
    setEnhancing(true);
    try {
      const file = src instanceof File ? src : new File([src], 'photo.png', { type: src.type || 'image/png' });
      const res = await schoolApi.enhanceStudentPhoto(orgId, file);
      setForm((f) => ({ ...f, photo_url: res.url }));
      setPhotoPreview(res.url);
      setPhotoBlob(null);
      toast.success(t('students.photoEnhanced', 'Photo enhanced with AI'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI enhancement failed');
    } finally { setEnhancing(false); }
  };

  const removePhoto = () => {
    setPhotoBlob(null); setPhotoPreview(''); setForm((f) => ({ ...f, photo_url: '' }));
  };

  // ---- Super admin: reveal saved login password ----
  const revealPassword = async (studentId) => {
    setRevealLoading(true);
    try {
      const data = await schoolApi.getStudentCredentials(studentId);
      setRevealCreds(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reveal password');
    } finally { setRevealLoading(false); }
  };


  const save = async () => {
    if (!form.name.trim()) { toast.error(t('common.name') + ' ' + t('common.required')); return; }
    if (!editing) {
      if (!form.email.trim() || !form.password || form.password.length < 6) {
        toast.error(t('people.loginRequired')); return;
      }
    }
    // Family "create new" validation applies in both create and edit modes
    if (familyMode === 'new' && form.new_family_name.trim()) {
      if (!form.new_family_email.trim() || !form.family_password) {
        toast.error(t('families.loginRequired', 'Family email and password are required')); return;
      }
    }
    setSaving(true);
    try {
      let familyAccountId = form.family_account_id || null;

      // Inline-create a new Family/Guardian and link it (create or edit)
      if (familyMode === 'new' && form.new_family_name.trim()) {
        const fam = await schoolApi.createFamily({
          organization_id: orgId,
          name: form.new_family_name.trim(),
          mobile: form.new_family_phone || null,
          email: form.new_family_email || null,
          password: form.family_password || null,
        });
        familyAccountId = fam.id;
      }

      const payload = {
        organization_id: orgId,
        name: form.name, gender: form.gender, dob: form.dob || null,
        blood_group: form.blood_group || null,
        address: form.address || null, phone: form.phone || null, email: form.email || null,
        create_login: true, password: form.password || null, role: form.role || 'student',
        family_account_id: familyAccountId,
        // Send '' (not null) so removing a photo actually clears it on the backend
        // (the update endpoint ignores null fields).
        photo_url: form.photo_url || '',
        special_needs: !!form.special_needs,
      };
      if (editing) {
        await schoolApi.updateStudent(editing.id, payload);
        toast.success(t('common.saved'));
      } else {
        await schoolApi.createStudent(payload);
        toast.success('Admission created');
      }
      setDialogOpen(false);
      loadStudents(); loadRefs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save student');
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try {
      await schoolApi.deleteStudent(deleteId);
      toast.success(t('common.deleted'));
      setDeleteId(null); loadStudents();
    } catch (e) { toast.error('Failed to delete'); }
  };

  // ---- Enroll a registry student into the active academic year (inline) ----
  const openEnroll = (s) => {
    setEnrollStudent(s);
    setEnrollForm({ class_id: '', section_id: '', roll_no: '' });
    setEnrollSections([]);
  };

  const onEnrollClass = async (class_id) => {
    setEnrollForm((f) => ({ ...f, class_id, section_id: '' }));
    setEnrollSections(await schoolApi.listSections(orgId, class_id, enrollYearId));
  };

  const saveEnroll = async () => {
    if (!enrollStudent || !enrollForm.class_id) { toast.error(t('common.required', 'Required')); return; }
    if (!enrollForm.section_id) { toast.error(t('enroll.sectionRequired', 'Please select a section')); return; }
    if (!enrollYearId) { toast.error(t('students.noActiveYear', 'No active academic year set')); return; }
    setEnrollSaving(true);
    try {
      const res = await schoolApi.createEnrollment({
        organization_id: orgId,
        student_id: enrollStudent.id,
        class_id: enrollForm.class_id,
        section_id: enrollForm.section_id || null,
        academic_year_id: enrollYearId,
        roll_no: enrollForm.roll_no || null,
      });
      toast.success(t('enroll.enrolled', 'Student enrolled'));
      const paired = res?.paired_enrolled || [];
      if (paired.length) {
        toast.success(t('enroll.pairedEnrolled', 'Also enrolled in paired class: ') + paired.join(', '));
      }
      setEnrollStudent(null);
      loadStudents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('enroll.enrollFailed', 'Failed to enroll student'));
    } finally { setEnrollSaving(false); }
  };

  // ---- Import flow ----
  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/school/students/import-excel/preview?organization_id=${orgId}`, fd);
      setImportPreview(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to preview file');
      setImportFile(null);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const runImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportProgress({ status: 'processing', processed: 0, total: importPreview?.total_rows || 0 });
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const r = await axios.post(`${API}/school/students/import-excel?organization_id=${orgId}`, fd);
      const jobId = r.data.job_id;
      if (!jobId) { throw new Error('No job started'); }

      let done = false;
      let polls = 0;
      const maxPolls = 600; // ~15 min safety cap
      while (!done && polls < maxPolls) {
        polls += 1;
        await new Promise((res) => setTimeout(res, 1500));
        let job;
        try {
          const jr = await axios.get(`${API}/school/students/import-jobs/${jobId}`);
          job = jr.data;
        } catch { continue; }
        setImportProgress({
          status: job.status,
          processed: job.processed || 0,
          total: job.total_ops || job.total || 0,
        });
        if (job.status === 'completed') {
          done = true;
          toast.success(job.message || 'Import complete');
          setImportOpen(false); setImportPreview(null); setImportFile(null); setImportProgress(null);
          loadStudents(); loadRefs();
        } else if (job.status === 'failed') {
          done = true;
          toast.error(job.message || 'Import failed');
          setImportProgress(null);
        }
      }
      if (!done) {
        toast.info(t('students.importBackground', 'Import is still running in the background. Refresh the list shortly.'));
        setImportProgress(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
      setImportProgress(null);
    } finally { setImporting(false); }
  };

  // ---------- Bulk credential generation ----------
  const openCredentials = async () => {
    setCredOpen(true); setCredSelected({}); setCredSearch(''); setCredShowAll(false);
    setCredLoadingList(true);
    try { const r = await schoolApi.scanStudentCredentials(orgId); setCredScan(r.students || []); }
    catch { toast.error('Failed to load students'); }
    finally { setCredLoadingList(false); }
  };

  const credFiltered = () => {
    const q = credSearch.trim().toLowerCase();
    return credScan.filter((s) => {
      if (!credShowAll && s.has_credentials) return false;
      if (q) {
        const nm = (s.name || '').toLowerCase();
        const adm = (s.admission_no || '').toLowerCase();
        if (!nm.includes(q) && !adm.includes(q)) return false;
      }
      return true;
    });
  };

  const credSelectedIds = Object.keys(credSelected).filter((id) => credSelected[id]);

  const toggleCredSelectAll = (checked) => {
    const next = { ...credSelected };
    credFiltered().forEach((s) => { next[s.id] = checked; });
    setCredSelected(next);
  };

  const runGenerate = async () => {
    if (credSelectedIds.length === 0) { toast.error('Select at least one student'); return; }
    setGenerating(true);
    try {
      const r = await schoolApi.generateStudentCredentials(orgId, credSelectedIds);
      toast.success(`${r.count} credential(s) generated`);
      setCredOpen(false);
      setPrintCards(r.generated || []);
      loadStudents();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setGenerating(false); }
  };

  const printCardsNow = () => {
    if (!printCards || printCards.length === 0) return;
    const html = buildCredentialCardsHtml(currentOrg, printCards);
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup blocked. Allow popups to print.'); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* noop */ } }, 500);
  };

  // ---------- Print credentials (class/section filter) ----------
  const openPrintFilter = () => {
    setPrintFilterOpen(true); setPrintClass(''); setPrintSection(''); setPrintSections([]);
  };

  const onPrintClassChange = async (cid) => {
    setPrintClass(cid); setPrintSection(''); setPrintSections([]);
    if (!cid) return;
    setPrintLoadingSecs(true);
    try { setPrintSections(await schoolApi.listSections(orgId, cid, selectedYearId)); }
    catch { toast.error('Failed to load sections'); }
    finally { setPrintLoadingSecs(false); }
  };

  const continuePrint = async () => {
    if (!printClass || !printSection) { toast.error('Select a class and a section'); return; }
    setReprintLoading(true); setReprintSearch('');
    try {
      const r = await schoolApi.listStudentsWithCredentials(orgId, printClass, printSection, selectedYearId);
      const list = r.students || [];
      setReprintList(list);
      const sel = {}; list.forEach((s) => { sel[s.id] = true; });
      setReprintSelected(sel);
      setPrintFilterOpen(false);
      setReprintOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load credentials'); }
    finally { setReprintLoading(false); }
  };

  const reprintFiltered = () => {
    const q = reprintSearch.trim().toLowerCase();
    if (!q) return reprintList;
    return reprintList.filter((s) => (s.name || '').toLowerCase().includes(q) || (s.admission_no || '').toLowerCase().includes(q));
  };

  const reprintSelectedIds = Object.keys(reprintSelected).filter((id) => reprintSelected[id]);

  const toggleReprintSelectAll = (checked) => {
    const next = { ...reprintSelected };
    reprintFiltered().forEach((s) => { next[s.id] = checked; });
    setReprintSelected(next);
  };

  const printSheetNow = () => {
    const chosen = reprintList.filter((s) => reprintSelected[s.id]);
    if (chosen.length === 0) { toast.error('Select at least one student'); return; }
    const html = buildCredentialSheetHtml(currentOrg, chosen);
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup blocked. Allow popups to print.'); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* noop */ } }, 500);
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <PageHeader
        title={t('students.title')} subtitle={t('students.subtitle')} icon={GraduationCap}
        actions={(
          <div className="flex flex-wrap gap-2">
            {isSuperAdmin && <Button variant="outline" onClick={runCleanupAcademicFields} disabled={cleaningReg} className="gap-2" data-testid="students-cleanup-academic-button"><Eraser className="w-4 h-4" /> {cleaningReg ? t('common.saving', 'Working...') : t('students.cleanupAcademic', 'Clean legacy class fields')}</Button>}
            {isSuperAdmin && <Button variant="outline" onClick={() => setAssignGuardianOpen(true)} disabled={assigningGuardian} className="gap-2" data-testid="students-assign-default-guardian-button"><UserPlus className="w-4 h-4" /> {assigningGuardian ? t('common.saving', 'Working...') : t('students.assignDefaultGuardian', 'Assign Default Guardian')}</Button>}
            {isSuperAdmin && <Button variant="outline" onClick={openCredentials} className="gap-2" data-testid="students-generate-credentials-button"><KeyRound className="w-4 h-4" /> {t('students.generateCreds', 'Generate Credentials')}</Button>}
            {isSuperAdmin && <Button variant="outline" onClick={openPrintFilter} className="gap-2" data-testid="students-print-credentials-button"><Printer className="w-4 h-4" /> {t('students.printCreds', 'Print Credentials')}</Button>}
            {canAdmin && <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-2" data-testid="students-import-button"><Upload className="w-4 h-4" /> {t('common.import', 'Import')}</Button>}
            <Button onClick={openCreate} className="gap-2" data-testid="students-new-admission-button"><Plus className="w-4 h-4" /> {t('students.new')}</Button>
          </div>
        )}
      />

      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="students-search-input" />
          </div>
          <Select value={filterClass || 'all'} onValueChange={(v) => setFilterClass(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-full sm:w-56" data-testid="students-class-filter"><SelectValue placeholder={t('students.class')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')} {t('students.class')}</SelectItem>
              {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted/40 shrink-0" data-testid="students-not-enrolled-toggle-row">
            <Checkbox checked={onlyNotEnrolled} onCheckedChange={(v) => setOnlyNotEnrolled(!!v)} data-testid="students-not-enrolled-toggle" />
            <span className="whitespace-nowrap">{t('students.onlyNotEnrolled', 'Only not enrolled')}</span>
          </label>
        </div>
      </Card>

      <Card>
        {loading ? (
          <div className="p-4"><LoadingState /></div>
        ) : students.length === 0 ? (
          <EmptyState icon={GraduationCap} title={t('common.noData')} description={t('students.subtitle')} actionLabel={t('students.new')} onAction={openCreate} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <SortableTH field="name" label={t('common.name')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                    <SortableTH field="admission_no" label={t('students.admissionNo')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                    <th className="px-4 py-3">{t('students.class')}</th>
                    <th className="px-4 py-3">{t('students.guardian')}</th>
                    <SortableTH field="status" label={t('common.status')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                    <th className="px-4 py-3 text-right">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s) => (
                    <tr key={s.id} className="border-b border-border last:border-0 hover:bg-muted/40" data-testid={`student-row-${s.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {s.photo_url ? (
                            <img src={s.photo_url} alt={s.name} className="w-8 h-8 rounded-full object-cover border border-border" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">{s.name?.charAt(0)}</div>
                          )}
                          <span className="font-medium">{s.name}</span>
                          {s.special_needs && <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50" data-testid={`student-special-badge-${s.id}`}>{t('students.specialNeedsShort', 'Special needs')}</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3 tabular-nums text-muted-foreground">{s.admission_no}</td>
                      <td className="px-4 py-3">{s.class_name || '-'} {s.section_name ? `/ ${s.section_name}` : ''}</td>
                      <td className="px-4 py-3">{s.family_name || s.parent_name || '-'}</td>
                      <td className="px-4 py-3"><Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">{t('common.active')}</Badge></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {canAdmin && (
                            <Button
                              variant="ghost" size="icon"
                              className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                              onClick={() => openEnroll(s)}
                              title={t('enroll.assign', 'Enroll')} aria-label={t('enroll.assign', 'Enroll')}
                              data-testid={`student-enroll-${s.id}`}
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost" size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted"
                            onClick={() => { setRevealCreds(null); setViewStudent(s); }}
                            title={t('common.view')} aria-label={t('common.view')}
                            data-testid={`student-view-${s.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10"
                            onClick={() => openEdit(s)}
                            title={t('common.edit')} aria-label={t('common.edit')}
                            data-testid={`student-edit-${s.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                            onClick={() => setDeleteId(s.id)}
                            title={t('common.delete')} aria-label={t('common.delete')}
                            data-testid={`student-delete-${s.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
          </>
        )}
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="student-dialog">
          <DialogHeader><DialogTitle>{editing ? t('common.edit') : t('students.new')}</DialogTitle></DialogHeader>

          {/* Student photo: upload / capture / AI enhance */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 border border-border rounded-lg p-4">
            <div className="relative shrink-0">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-muted flex items-center justify-center border border-border" data-testid="student-photo-preview">
                {photoPreview ? (
                  <img src={photoPreview} alt={form.name || 'student'} className="w-full h-full object-cover" />
                ) : (
                  <Camera className="w-8 h-8 text-muted-foreground" />
                )}
              </div>
              {(photoUploading || enhancing) && (
                <div className="absolute inset-0 rounded-full bg-background/70 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              )}
            </div>
            <div className="flex-1 w-full">
              <p className="text-sm font-semibold mb-1">{t('students.photo', 'Student Photo')}</p>
              <p className="text-xs text-muted-foreground mb-3">{t('students.photoHint', 'Upload a file or capture with the camera, then optionally enhance it into a professional portrait with AI.')}</p>
              <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoFile} className="hidden" data-testid="student-photo-file-input" />
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => photoInputRef.current?.click()} disabled={photoUploading || enhancing} className="gap-1" data-testid="student-photo-upload-btn">
                  <Upload className="w-4 h-4" /> {t('common.upload', 'Upload')}
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={openCamera} disabled={photoUploading || enhancing} className="gap-1" data-testid="student-photo-camera-btn">
                  <Camera className="w-4 h-4" /> {t('students.capture', 'Camera')}
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={enhancePhoto} disabled={(!photoPreview && !form.photo_url) || photoUploading || enhancing} className="gap-1 text-purple-600 border-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950/30" data-testid="student-photo-enhance-btn">
                  {enhancing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {t('students.enhanceAi', 'Enhance with AI')}
                </Button>
                {photoPreview && (
                  <Button type="button" size="sm" variant="ghost" onClick={removePhoto} disabled={photoUploading || enhancing} className="gap-1 text-red-600" data-testid="student-photo-remove-btn">
                    <X className="w-4 h-4" /> {t('common.remove', 'Remove')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>{t('common.name')} *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="student-name-input" />
            </div>
            <div>
              <Label>{t('common.gender')}</Label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Male">{t('common.male')}</SelectItem><SelectItem value="Female">{t('common.female')}</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>{t('students.dob')}</Label><Input type="date" value={form.dob || ''} onChange={(e) => setForm({ ...form, dob: e.target.value })} /></div>
            <div className="sm:col-span-2 rounded-md bg-muted/40 border border-border px-3 py-2 text-xs text-muted-foreground" data-testid="student-registry-note">
              {t('students.registryNote', 'This is the permanent school registry. Assign a class, section and roll number later from the Enrollment page for a specific academic year.')}
            </div>
            <div><Label>{t('students.bloodGroup')}</Label><Input value={form.blood_group || ''} onChange={(e) => setForm({ ...form, blood_group: e.target.value })} /></div>
            <div><Label>{t('common.phone')}</Label><Input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div>
              <Label>{t('common.email')} *</Label>
              <div className="flex gap-2">
                <Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="student-email-input" />
                {!editing && (
                  <Button type="button" variant="outline" onClick={generateLogin} disabled={genLoading} className="shrink-0 gap-1" data-testid="student-generate-login-btn" title={t('students.generateLogin', 'Auto-generate email & password')}>
                    {genLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {t('students.generate', 'Generate')}
                  </Button>
                )}
              </div>
            </div>
            <div className="sm:col-span-2"><Label>{t('students.address')}</Label><Input value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="sm:col-span-2">
              <label className="flex items-start gap-2.5 rounded-md border border-border p-3 cursor-pointer hover:bg-muted/40" data-testid="student-special-needs-row">
                <Checkbox checked={!!form.special_needs} onCheckedChange={(v) => setForm({ ...form, special_needs: !!v })} className="mt-0.5" data-testid="student-special-needs-checkbox" />
                <span>
                  <span className="text-sm font-medium block">{t('students.specialNeeds', 'Special needs (not graded)')}</span>
                  <span className="text-xs text-muted-foreground">{t('students.specialNeedsHint', 'Enrolled in a normal class but excluded from grading, ranking, missing-grades, class summary and AI analysis.')}</span>
                </span>
              </label>
            </div>
          </div>

          {!editing && (
            <div className="border-t border-border pt-4 mt-2">
              <p className="text-sm font-semibold mb-2">{t('people.loginAccount')} *</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>{t('people.password')} *</Label>
                  <div className="relative">
                    <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="pr-9" data-testid="student-password-input" />
                    <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1} data-testid="student-password-toggle" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>{t('people.role')} *</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="student-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{PICKABLE_ROLES.map((r) => <SelectItem key={r.key} value={r.key}>{t(r.labelKey)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {(
            <div className="border-t border-border pt-4 mt-2">
              <p className="text-sm font-semibold mb-2">{t('students.guardian', 'Family / Guardian')}</p>
              <div className="flex gap-2 mb-3">
                <Button type="button" size="sm" variant={familyMode === 'existing' ? 'default' : 'outline'} onClick={() => setFamilyMode('existing')} data-testid="family-mode-existing">{t('students.selectParent', 'Select existing')}</Button>
                <Button type="button" size="sm" variant={familyMode === 'new' ? 'default' : 'outline'} onClick={() => setFamilyMode('new')} data-testid="family-mode-new">{t('students.createParent', 'Create new')}</Button>
              </div>
              {familyMode === 'existing' ? (
                <div className="relative">
                  {form.family_account_id && selectedFamilyLabel ? (
                    <div className="flex items-center justify-between border border-border rounded-md px-3 py-2 text-sm" data-testid="selected-family">
                      <span className="font-medium">{selectedFamilyLabel}</span>
                      <Button type="button" size="sm" variant="ghost" onClick={() => { setForm({ ...form, family_account_id: '' }); setSelectedFamilyLabel(''); }}>{t('common.change', 'Change')}</Button>
                    </div>
                  ) : (
                    <>
                      <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                      <Input
                        className="pl-9"
                        placeholder={t('students.searchFamily', 'Search family by name, phone, or code...')}
                        value={familySearch}
                        onChange={(e) => setFamilySearch(e.target.value)}
                        data-testid="family-search-input"
                      />
                      {familyResults.length > 0 && (
                        <div className="mt-1 max-h-48 overflow-y-auto border border-border rounded-md divide-y divide-border" data-testid="family-results">
                          {familyResults.map((f) => (
                            <button
                              type="button"
                              key={f.id}
                              onClick={() => pickFamily(f)}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60 focus:bg-muted/60 focus-visible:outline-none"
                              data-testid={`family-result-${f.id}`}
                            >
                              <span className="font-medium">{f.name_en || f.name}</span>
                              <span className="text-muted-foreground"> {f.mobile ? `· ${f.mobile}` : ''} · {f.code}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {familySearch && familyResults.length === 0 && (
                        <p className="text-xs text-muted-foreground mt-1">{t('students.noFamilyFound', 'No family found. Try "Create new".')}</p>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input placeholder={t('students.parentName', 'Family/Guardian name')} value={form.new_family_name} onChange={(e) => setForm({ ...form, new_family_name: e.target.value })} data-testid="new-family-name-input" />
                  <Input placeholder={t('common.phone')} value={form.new_family_phone} onChange={(e) => setForm({ ...form, new_family_phone: e.target.value })} />
                  <Input type="email" placeholder={`${t('common.email')} *`} value={form.new_family_email} onChange={(e) => setForm({ ...form, new_family_email: e.target.value })} data-testid="new-family-email-input" />
                  <Input type="password" placeholder={`${t('people.password')} *`} value={form.family_password} onChange={(e) => setForm({ ...form, family_password: e.target.value })} data-testid="new-family-password-input" />
                </div>
              )}
              {familyMode === 'new' && form.new_family_name.trim() && (
                <p className="text-[11px] text-muted-foreground mt-2">{t('people.loginRequired')} ({t('role.parent', 'Parent')})</p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={save} disabled={saving} data-testid="student-save-button">{saving ? t('common.saving') : t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View profile */}
      <Dialog open={!!viewStudent} onOpenChange={(o) => { if (!o) { setViewStudent(null); setRevealCreds(null); } }}>
        <DialogContent data-testid="student-profile-dialog">
          <DialogHeader><DialogTitle>{t('students.profile')}</DialogTitle></DialogHeader>
          {viewStudent && (
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-3 mb-3">
                {viewStudent.photo_url ? (
                  <img src={viewStudent.photo_url} alt={viewStudent.name} className="w-12 h-12 rounded-full object-cover border border-border" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-lg font-semibold text-primary">{viewStudent.name?.charAt(0)}</div>
                )}
                <div><p className="font-semibold text-base">{viewStudent.name}</p><p className="text-muted-foreground">{viewStudent.admission_no}</p></div>
              </div>
              <Row label={t('students.class')} value={`${viewStudent.class_name || '-'} ${viewStudent.section_name ? '/ ' + viewStudent.section_name : ''}`} />
              <Row label={t('common.gender')} value={viewStudent.gender} />
              <Row label={t('students.dob')} value={viewStudent.dob} />
              <Row label={t('students.guardian')} value={viewStudent.family_name || viewStudent.parent_name} />
              <Row label={t('common.phone')} value={viewStudent.phone} />
              <Row label={t('common.email')} value={viewStudent.email} />
              <Row label={t('students.address')} value={viewStudent.address} />

              {/* Super admin: reveal login password */}
              {isSuperAdmin && (
                <div className="border-t border-border pt-3 mt-3">
                  {revealCreds ? (
                    <div className="rounded-md bg-muted/50 p-3 space-y-1" data-testid="student-revealed-credentials">
                      <div className="flex justify-between"><span className="text-muted-foreground">{t('common.email')}</span><span className="font-mono">{revealCreds.email || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">{t('people.password')}</span><span className="font-mono font-semibold">{revealCreds.password || t('students.noStoredPassword', 'Not available')}</span></div>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" className="gap-2" onClick={() => revealPassword(viewStudent.id)} disabled={revealLoading} data-testid="student-reveal-password-btn">
                      {revealLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} {t('students.revealPassword', 'Reveal login password')}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Webcam capture dialog */}
      <Dialog open={camOpen} onOpenChange={(o) => { if (!o) closeCamera(); }}>
        <DialogContent data-testid="student-camera-dialog">
          <DialogHeader><DialogTitle>{t('students.capturePhoto', 'Capture Photo')}</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-3">
            <div className="w-full rounded-lg overflow-hidden bg-black aspect-video flex items-center justify-center">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeCamera}>{t('common.cancel')}</Button>
            <Button onClick={capturePhoto} className="gap-2" data-testid="student-camera-capture-btn"><Camera className="w-4 h-4" /> {t('students.capture', 'Capture')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={assignGuardianOpen} onOpenChange={(o) => { if (!assigningGuardian) setAssignGuardianOpen(o); }}>
        <AlertDialogContent data-testid="assign-default-guardian-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('students.assignDefaultGuardian', 'Assign Default Guardian')}</AlertDialogTitle>
            <AlertDialogDescription>
              This will assign Normal Guardian (code: 41110001) to all students who currently have no guardian assigned. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={assigningGuardian}>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); runAssignDefaultGuardian(); }} disabled={assigningGuardian} data-testid="confirm-assign-default-guardian">
              {assigningGuardian ? t('common.saving', 'Working...') : t('common.continue', 'Continue')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>{t('common.delete')}?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-student">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Enroll-from-registry dialog */}
      <Dialog open={!!enrollStudent} onOpenChange={(o) => { if (!o) setEnrollStudent(null); }}>
        <DialogContent data-testid="student-enroll-dialog">
          <DialogHeader>
            <DialogTitle>{t('enroll.assign', 'Enroll student')}</DialogTitle>
            <DialogDescription>
              {enrollStudent ? `${enrollStudent.name}${enrollStudent.admission_no ? ` · ${enrollStudent.admission_no}` : ''}` : ''}
              {(years.find((y) => y.id === enrollYearId)?.name) ? ` — ${years.find((y) => y.id === enrollYearId)?.name}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('students.class')} *</Label>
                <Select value={enrollForm.class_id || ''} onValueChange={onEnrollClass}>
                  <SelectTrigger data-testid="student-enroll-class"><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('students.section')} *</Label>
                <Select value={enrollForm.section_id || ''} onValueChange={(v) => setEnrollForm({ ...enrollForm, section_id: v })} disabled={!enrollForm.class_id}>
                  <SelectTrigger data-testid="student-enroll-section"><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent>{enrollSections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t('students.rollNo', 'Roll number')}</Label>
              <Input value={enrollForm.roll_no} onChange={(e) => setEnrollForm({ ...enrollForm, roll_no: e.target.value })} placeholder={t('enroll.rollAuto', 'Leave blank to auto-generate')} data-testid="student-enroll-roll-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollStudent(null)}>{t('common.cancel')}</Button>
            <Button onClick={saveEnroll} disabled={enrollSaving || !enrollForm.class_id || !enrollForm.section_id} data-testid="student-enroll-save-button">{enrollSaving ? t('common.saving') : t('enroll.assign', 'Enroll')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog (match-only, background job) */}
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportPreview(null); setImportFile(null); setImportProgress(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="students-import-dialog">
          <DialogHeader>
            <DialogTitle>{t('students.importTitle', 'Import Students')}</DialogTitle>
            <DialogDescription>
              {t('students.importHint', 'Upload an .xlsx with columns: full_name, gender_id (1=Male, 2=Female), date_of_birth, mobile, admission_date, class_name, section_name, guardian_name, email, password. Match-only: Class, Section and Guardian must already exist — rows referencing anything missing are skipped. A student login is created for each row.')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onPickFile} className="hidden" id="students-file-input" data-testid="students-file-input" />
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="students-choose-file-button">
                {importFile ? importFile.name : t('students.chooseFile', 'Choose Excel file')}
              </Button>
            </div>

            {importPreview && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-4 text-sm">
                  <span><strong>{importPreview.total_rows}</strong> {t('students.rows', 'rows')}</span>
                  <span className="text-emerald-600"><strong>{importPreview.to_create}</strong> {t('staff.toCreate', 'to create')}</span>
                  <span className="text-blue-600"><strong>{importPreview.duplicates}</strong> {t('subjects.duplicates', 'duplicates')}</span>
                  <span className="text-red-600"><strong>{importPreview.skipped}</strong> {t('subjects.skipped', 'skipped')}</span>
                </div>
                <div className="max-h-72 overflow-y-auto border border-border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">{t('staff.action', 'Action')}</th>
                        <th className="px-2 py-1 text-left">{t('common.name', 'Name')}</th>
                        <th className="px-2 py-1 text-left">{t('students.class', 'Class')}</th>
                        <th className="px-2 py-1 text-left">{t('sections.title', 'Section')}</th>
                        <th className="px-2 py-1 text-left">{t('students.guardian', 'Guardian')}</th>
                        <th className="px-2 py-1 text-left">{t('subjects.reason', 'Reason')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.preview_data.map((r, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-2 py-1"><Badge variant="outline" className={r.action === 'create' ? 'text-emerald-700' : r.action === 'duplicate' ? 'text-blue-700' : 'text-red-700'}>{r.action}</Badge></td>
                          <td className="px-2 py-1">{r.name}</td>
                          <td className="px-2 py-1">{r.class_name || '-'}</td>
                          <td className="px-2 py-1">{r.section_name || '-'}</td>
                          <td className="px-2 py-1">{r.guardian_name || '-'}</td>
                          <td className="px-2 py-1 text-muted-foreground">{r.reason || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {importPreview.to_create === 0 && (
                  <p className="text-xs text-amber-600">{t('students.nothingToImport', 'Nothing to import. Make sure Classes/Sections and Family/Guardians are imported first, and emails are not already used.')}</p>
                )}
              </div>
            )}

            {importProgress && (
              <div className="space-y-1" data-testid="students-import-progress">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{importProgress.status === 'processing' ? t('common.importing', 'Importing...') : importProgress.status}</span>
                  <span>{importProgress.processed}{importProgress.total ? ` / ${importProgress.total}` : ''}</span>
                </div>
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-primary transition-all duration-300" style={{ width: importProgress.total ? `${Math.min(100, Math.round((importProgress.processed / importProgress.total) * 100))}%` : '40%' }} />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} disabled={importing}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={runImport} disabled={!importPreview || importPreview.to_create === 0 || importing} data-testid="students-run-import-button">
              {importing ? t('common.importing', 'Importing...') : t('staff.confirmImport', 'Confirm Import')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Credentials */}
      <Dialog open={credOpen} onOpenChange={setCredOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="students-credentials-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" /> {t('students.generateCreds', 'Generate Credentials')}</DialogTitle>
            <DialogDescription>{t('students.credDesc', 'Select students to generate a username (email) and a secure password. Generated cards can be printed right after.')}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder={t('students.credSearch', 'Filter by name or admission no...')} value={credSearch} onChange={(e) => setCredSearch(e.target.value)} className="pl-9" data-testid="students-cred-search" />
            </div>
            <label className="flex items-center gap-2 text-sm whitespace-nowrap cursor-pointer select-none px-1">
              <Checkbox checked={credShowAll} onCheckedChange={(v) => setCredShowAll(!!v)} data-testid="students-cred-showall" />
              {t('students.credShowAll', 'Include already-generated')}
            </label>
          </div>

          {credShowAll && (
            <div className="flex items-start gap-2 text-xs rounded-md border border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:border-amber-900/50 dark:text-amber-300 p-2">
              <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
              <span>{t('students.credRegenWarn', 'Regenerating overwrites the current password — the student must use the new one to sign in.')}</span>
            </div>
          )}

          <div className="flex items-center justify-between border-y border-border py-2">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none">
              <Checkbox
                checked={credFiltered().length > 0 && credFiltered().every((s) => credSelected[s.id])}
                onCheckedChange={(v) => toggleCredSelectAll(!!v)}
                data-testid="students-cred-selectall"
              />
              {t('students.credSelectAll', 'Select All')}
            </label>
            <span className="text-xs text-muted-foreground" data-testid="students-cred-selected-count">{credSelectedIds.length} {t('students.credSelected', 'selected')}</span>
          </div>

          <div className="flex-1 overflow-y-auto -mx-1 px-1">
            {credLoadingList ? (
              <div className="py-8"><LoadingState /></div>
            ) : credFiltered().length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {credShowAll ? t('students.credEmptyAll', 'No students found.') : t('students.credEmpty', 'All students already have credentials. Toggle "Include already-generated" to regenerate.')}
              </div>
            ) : (
              <div className="divide-y divide-border">
                {credFiltered().map((s) => (
                  <label key={s.id} className="flex items-center gap-3 py-2.5 cursor-pointer hover:bg-muted/40 rounded px-1" data-testid={`students-cred-row-${s.id}`}>
                    <Checkbox checked={!!credSelected[s.id]} onCheckedChange={() => setCredSelected((p) => ({ ...p, [s.id]: !p[s.id] }))} data-testid={`students-cred-check-${s.id}`} />
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-semibold text-indigo-700 flex-none">{(s.name || '?').charAt(0)}</div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{s.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{s.admission_no ? `ADM: ${s.admission_no}` : ''}{s.email ? `  ·  ${s.email}` : (s.admission_no ? '' : t('students.credNoEmail', 'No email — one will be generated'))}</div>
                    </div>
                    {s.has_credentials && <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50 gap-1"><KeyRound className="w-3 h-3" /> {t('students.credHas', 'Generated')}</Badge>}
                  </label>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCredOpen(false)} disabled={generating}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={runGenerate} disabled={generating || credSelectedIds.length === 0} className="gap-2" data-testid="students-cred-generate-button">
              <KeyRound className="w-4 h-4" /> {generating ? t('students.credGenerating', 'Generating...') : `${t('students.credGenerateBtn', 'Generate')} (${credSelectedIds.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Preview (right after generate) */}
      <Dialog open={!!printCards} onOpenChange={(o) => { if (!o) setPrintCards(null); }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="students-print-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Printer className="w-5 h-5" /> {t('students.printPreview', 'Credential Cards — Print Preview')}</DialogTitle>
            <DialogDescription>{(printCards?.length || 0)} {t('students.cardsCount', 'card(s) ready to print.')}</DialogDescription>
          </DialogHeader>
          <style dangerouslySetInnerHTML={{ __html: CARD_CSS }} />
          <div className="flex-1 overflow-y-auto bg-muted/40 rounded-lg p-4">
            <div className="flex flex-wrap gap-5 justify-center">
              {(printCards || []).map((c) => (
                <div key={c.id} dangerouslySetInnerHTML={{ __html: cardMarkup(currentOrg, c) }} />
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrintCards(null)}>{t('common.close', 'Close')}</Button>
            <Button onClick={printCardsNow} className="gap-2" data-testid="students-print-button"><Printer className="w-4 h-4" /> {t('students.printAll', 'Print')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Credentials — class/section filter */}
      <Dialog open={printFilterOpen} onOpenChange={setPrintFilterOpen}>
        <DialogContent className="max-w-md" data-testid="students-print-filter-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Printer className="w-5 h-5" /> {t('students.printCreds', 'Print Credentials')}</DialogTitle>
            <DialogDescription>{t('students.printFilterDesc', 'Select a class and section to print ID cards for students with credentials.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label>{t('classes.title', 'Class')}</Label>
              <Select value={printClass} onValueChange={onPrintClassChange}>
                <SelectTrigger data-testid="students-print-class"><SelectValue placeholder={t('students.selectClass', 'Select class')} /></SelectTrigger>
                <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('sections.title', 'Section')}</Label>
              <Select value={printSection} onValueChange={setPrintSection} disabled={!printClass || printLoadingSecs}>
                <SelectTrigger data-testid="students-print-section"><SelectValue placeholder={printLoadingSecs ? t('common.loading', 'Loading...') : t('students.selectSection', 'Select section')} /></SelectTrigger>
                <SelectContent>{printSections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrintFilterOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={continuePrint} disabled={!printClass || !printSection || reprintLoading} className="gap-2" data-testid="students-print-continue-button">
              {reprintLoading ? t('common.loading', 'Loading...') : t('common.continue', 'Continue')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Credentials — cards */}
      <Dialog open={reprintOpen} onOpenChange={setReprintOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="students-reprint-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Printer className="w-5 h-5" /> {t('students.printCreds', 'Print Credentials')}</DialogTitle>
            <DialogDescription>{t('students.reprintDesc', 'Select the students to print — cards are laid out 8 per A4 page.')}</DialogDescription>
          </DialogHeader>
          <style dangerouslySetInnerHTML={{ __html: CARD_CSS }} />

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder={t('students.reprintSearch', 'Filter by name...')} value={reprintSearch} onChange={(e) => setReprintSearch(e.target.value)} className="pl-9" data-testid="students-reprint-search" />
            </div>
            <label className="flex items-center gap-2 text-sm font-medium whitespace-nowrap cursor-pointer select-none px-1">
              <Checkbox
                checked={reprintFiltered().length > 0 && reprintFiltered().every((s) => reprintSelected[s.id])}
                onCheckedChange={(v) => toggleReprintSelectAll(!!v)}
                data-testid="students-reprint-selectall"
              />
              {t('students.credSelectAll', 'Select All')}
            </label>
            <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="students-reprint-selected-count">{reprintSelectedIds.length} {t('students.credSelected', 'selected')}</span>
          </div>

          <div className="flex-1 overflow-y-auto bg-muted/40 rounded-lg p-4">
            {reprintLoading ? (
              <div className="py-8"><LoadingState /></div>
            ) : reprintFiltered().length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                {reprintList.length === 0
                  ? t('students.reprintEmpty', 'No students in this class/section have generated credentials yet.')
                  : t('students.reprintNoMatch', 'No students match your search.')}
              </div>
            ) : (
              <div className="flex flex-wrap gap-5 justify-center">
                {reprintFiltered().map((c) => (
                  <div key={c.id} className="relative" data-testid={`students-reprint-card-${c.id}`}>
                    <label className="absolute -top-2 -left-2 z-10 bg-background border border-border rounded-md p-1.5 shadow-sm cursor-pointer">
                      <Checkbox checked={!!reprintSelected[c.id]} onCheckedChange={() => setReprintSelected((p) => ({ ...p, [c.id]: !p[c.id] }))} data-testid={`students-reprint-check-${c.id}`} />
                    </label>
                    <div
                      className={reprintSelected[c.id] ? 'ring-2 ring-primary ring-offset-2 ring-offset-background rounded-2xl transition-shadow' : 'opacity-60 transition-opacity'}
                      dangerouslySetInnerHTML={{ __html: cardMarkup(currentOrg, c) }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReprintOpen(false)}>{t('common.close', 'Close')}</Button>
            <Button onClick={printSheetNow} disabled={reprintSelectedIds.length === 0} className="gap-2" data-testid="students-reprint-print-button">
              <Printer className="w-4 h-4" /> {t('students.printSelected', 'Print')} ({reprintSelectedIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between border-b border-border py-1.5"><span className="text-muted-foreground">{label}</span><span className="font-medium">{value || '-'}</span></div>
);
